/*!
 * Copyright 2024 - Swiss Data Science Center (SDSC)
 * A partnership between École Polytechnique Fédérale de Lausanne (EPFL) and
 * Eidgenössische Technische Hochschule Zürich (ETHZ).
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS, WITHOUT
 * WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
  faExclamationTriangle,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { ReactNode, useRef } from "react";
import {
  CheckCircleFill,
  CircleFill,
  Clock,
  ExclamationTriangleFill,
  GearFill,
  Hourglass,
  Icon,
} from "react-bootstrap-icons";
import { PopoverBody, PopoverHeader, UncontrolledPopover } from "reactstrap";

import RenkuBadge from "~/components/renkuBadge/RenkuBadge";
import {
  getLauncherCategoryDefinition,
  JOB_STOPPING_BUTTON_LABEL,
  JOB_STOPPING_TITLE,
  sessionLauncherKindToCategory,
} from "~/features/sessionsV2/session.utils";
import { Loader } from "../../../../components/Loader";
import { TimeCaption } from "../../../../components/TimeCaption";
import { useGetResourcePoolsQuery } from "../../api/computeResources.api";
import type { SessionLauncher } from "../../api/sessionLaunchersV2.api";
import { UsageAvailable } from "../../session.utils";
import {
  JOB_TITLE,
  SESSION_STATES,
  SESSION_STYLES,
  SESSION_TITLE,
  SESSION_TITLE_DASHBOARD,
} from "../../SessionStyles.constants";
import {
  LauncherCategory,
  LauncherCategoryDefinition,
  SessionLauncherKind,
  SessionStatus,
  SessionStatusState,
  SessionV2,
} from "../../sessionsV2.types";

import statusStyles from "./SessionStatus.module.scss";

interface PrettySessionErrorMessageProps {
  message: string | null | undefined;
}
function PrettySessionErrorMessage({
  message,
}: PrettySessionErrorMessageProps) {
  // eslint-disable-next-line spellcheck/spell-checker
  if (message?.includes("Insufficient nvidia.com/gpu")) {
    return (
      <>
        <p className="mb-2">
          Scheduling error: there are not enough GPUs available to start or
          resume the session.
        </p>
        <h3 className="fs-6">Original error message:</h3>
        <p className="mb-0">{message}</p>
      </>
    );
  }

  if (
    message?.includes("nodes are available") ||
    message?.includes("The resource quota has been exceeded.")
  ) {
    return (
      <>
        <p className="mb-2">
          Scheduling error: one or more compute resources have been exhausted in
          the resource pool.
        </p>
        <h3 className="fs-6">Original error message:</h3>
        <p className="mb-0">{message}</p>
      </>
    );
  }

  return <>{message}</>;
}

function MissingHibernationInfo() {
  const ref = useRef<HTMLSpanElement>(null);

  return (
    <>
      <span ref={ref}>
        <FontAwesomeIcon className="ms-1" icon={faExclamationTriangle} />
      </span>
      <UncontrolledPopover placement="bottom" target={ref} trigger="hover">
        <PopoverHeader>Missing information</PopoverHeader>
        <PopoverBody>
          Information about when this session was paused is not available.
        </PopoverBody>
      </UncontrolledPopover>
    </>
  );
}

interface ActiveSessionV2Props {
  session: SessionV2;
  variant?: "card" | "list";
  includeIcon?: boolean;
}
interface ActiveSessionDescV2Props extends ActiveSessionV2Props {
  showInfoDetails?: boolean;
}
interface ActiveSessionTitleV2Props {
  session: SessionV2;
  launcher?: SessionLauncher;
}

function UnknownStatusBadge() {
  return (
    <>
      <RenkuBadge color="warning">
        <ExclamationTriangleFill className="me-1" />
        Unknown status
      </RenkuBadge>
    </>
  );
}

function WarningStatusBadge({
  icon,
  label,
}: {
  icon: "circle" | "icon" | "loader";
  label: string;
}) {
  const color = icon === "loader" ? "light" : "warning";
  const renderedIcon =
    icon === "loader" ? (
      <Loader className="me-1" size={12} inline />
    ) : icon === "icon" ? (
      <GearFill className={cx("me-1", statusStyles.spinIcon)} />
    ) : (
      <CircleFill className="me-1" />
    );

  return (
    <RenkuBadge className="fw-normal" color={color} pill>
      {renderedIcon}
      {label}
    </RenkuBadge>
  );
}

function LoaderStatusBadge({ label }: { label: string }) {
  return <WarningStatusBadge icon="loader" label={label} />;
}

function HibernatedStatusBadge({ label }: { label: string }) {
  return (
    <RenkuBadge className="fw-normal" color="light" pill>
      <CircleFill className="me-1" />
      {label}
    </RenkuBadge>
  );
}

function FailedStatusBadge({ label }: { label: string }) {
  return (
    <RenkuBadge className="fw-normal" color="danger" pill>
      <ExclamationTriangleFill className="me-1" />
      {label}
    </RenkuBadge>
  );
}

function SuccessStatusBadge({ label }: { label: string }) {
  return (
    <RenkuBadge className="fw-normal" color="success" pill>
      <CheckCircleFill className="me-1" />
      {label}
    </RenkuBadge>
  );
}

interface BuildSessionStatusBadgeProps {
  state: SessionStatusState;
  launcherCategory: LauncherCategory;
  launcherDefinition: LauncherCategoryDefinition;
  image?: string;
  jobStoppingBadgeLabel: string;
}

function BuildSessionStatusBadge({
  state,
  launcherCategory,
  launcherDefinition,
  image,
  jobStoppingBadgeLabel,
}: BuildSessionStatusBadgeProps) {
  const { text } = launcherDefinition;

  switch (state) {
    case "starting":
      return (
        <LoaderStatusBadge
          label={`${text.state.starting} ${launcherCategory === "session" ? text.inline : ""}`}
        />
      );

    case "stopping":
      return (
        <LoaderStatusBadge
          label={
            launcherCategory === "job"
              ? jobStoppingBadgeLabel
              : text.delete.action
          }
        />
      );
    case "hibernated":
      return <HibernatedStatusBadge label={text.state.hibernated} />;
    case "failed":
      return <FailedStatusBadge label={text.state.failed} />;
    case "running":
      if (launcherCategory === "job") {
        return <WarningStatusBadge icon="icon" label="Running" />;
      }
      if (image) {
        return <SuccessStatusBadge label={`Running ${text.display}`} />;
      }
      return (
        <WarningStatusBadge icon="circle" label={`Running ${text.display}`} />
      );
    case "succeeded":
      if (launcherCategory !== "job") {
        return <UnknownStatusBadge />;
      }
      return <SuccessStatusBadge label="Completed" />;
    default:
      return <UnknownStatusBadge />;
  }
}

export function SessionStatusV2Badge({ session }: ActiveSessionV2Props) {
  const { status, image } = session;
  const state = status.state;
  const launcherCategory = sessionLauncherKindToCategory(session.session_type);
  const launcherDefinition = getLauncherCategoryDefinition(launcherCategory);

  return (
    <div className={cx("d-flex", "flex-row", "gap-2", "align-items-center")}>
      <BuildSessionStatusBadge
        state={state}
        launcherCategory={launcherCategory}
        launcherDefinition={launcherDefinition}
        image={image}
        jobStoppingBadgeLabel={JOB_STOPPING_BUTTON_LABEL}
      />
    </div>
  );
}

function resolveSessionStatusLabel({
  state,
  variant,
  sessionType,
}: {
  state: SessionStatusState;
  variant: "card" | "list";
  sessionType: SessionLauncherKind;
}): string {
  const launcherCategory = sessionLauncherKindToCategory(sessionType);

  if (state === "stopping" && launcherCategory === "job") {
    return JOB_STOPPING_TITLE[variant];
  }

  const isSessionLauncher = launcherCategory === "session";
  const isListVariant = variant === "list";

  const titles = isSessionLauncher
    ? isListVariant
      ? SESSION_TITLE_DASHBOARD
      : SESSION_TITLE
    : JOB_TITLE;

  return titles[state] ?? titles.default;
}

interface SessionStatusStyles {
  textColorCard: string;
  textColorList: string;
  bgColor: string;
  bgOpacity: number;
  borderColor: string;
  sessionLine: string;
  sessionIcon: string;
  jobIcon: Icon;
}

export function getSessionStatusStyles(
  session: {
    status: { state: string };
    image?: string;
  },
  launcherCategory: LauncherCategory,
): SessionStatusStyles {
  const { status, image } = session;
  const state = status.state;

  if (state === SESSION_STATES.RUNNING && launcherCategory === "session") {
    return image ? SESSION_STYLES.SUCCESS : SESSION_STYLES.WARNING;
  }

  if (state === SESSION_STATES.RUNNING && launcherCategory === "job") {
    return SESSION_STYLES.RUNNING_JOB;
  }

  const stateStyleMap: Record<string, SessionStatusStyles> = {
    [SESSION_STATES.STARTING]: SESSION_STYLES.WARNING,
    [SESSION_STATES.STOPPING]: SESSION_STYLES.STOPPING,
    [SESSION_STATES.HIBERNATED]: SESSION_STYLES.HIBERNATED,
    [SESSION_STATES.FAILED]: SESSION_STYLES.FAILED,
    [SESSION_STATES.SUCCEEDED]: SESSION_STYLES.SUCCESS,
  };

  return stateStyleMap[state] ?? SESSION_STYLES.DEFAULT;
}

export function SessionStatusV2Label({
  session,
  variant = "card",
}: ActiveSessionV2Props) {
  const { status, image } = session;
  const launcherCategory = sessionLauncherKindToCategory(session.session_type);
  const styles = getSessionStatusStyles({ status, image }, launcherCategory);
  const statusLabel = resolveSessionStatusLabel({
    state: status.state,
    variant,
    sessionType: session.session_type,
  });

  return (
    <div
      className={cx(
        "d-flex",
        "flex-row",
        "gap-2",
        "align-items-center",
        "fs-4",
        "fw-bold",
      )}
    >
      <span
        className={
          variant === "list" ? styles.textColorList : styles.textColorCard
        }
      >
        {statusLabel}
      </span>
    </div>
  );
}

export function SessionStatusV2Description({
  session,
  showInfoDetails = true,
  includeIcon = true,
}: ActiveSessionDescV2Props) {
  const { started, status, resource_class_id, session_type, job_completed_at } =
    session;
  const launcherCategory = sessionLauncherKindToCategory(session_type);
  return (
    <div
      className={cx(
        "time-caption",
        "d-flex",
        "flex-row",
        "gap-2",
        "align-items-center",
      )}
    >
      {(started || status.state === "succeeded") && (
        <SessionStatusV2Text
          startTimestamp={started ?? ""}
          jobCompletedAt={job_completed_at}
          status={status}
          resourceClassId={resource_class_id}
          launcherCategory={launcherCategory}
          includeIcon={includeIcon}
        />
      )}
      {showInfoDetails && (
        <SessionListRowStatusExtraDetailsV2 status={status} />
      )}
    </div>
  );
}

interface StatusExtraDetailsV2Props {
  status: SessionStatus;
}
function SessionListRowStatusExtraDetailsV2({
  status,
}: StatusExtraDetailsV2Props) {
  const ref = useRef<HTMLElement>(null);
  if (!status.message) return null;

  const popover = (
    <UncontrolledPopover
      target={ref}
      trigger="hover focus"
      placement="bottom"
      onClick={(event) => {
        event.stopPropagation();
      }}
    >
      <PopoverHeader>Kubernetes pod status</PopoverHeader>
      <PopoverBody>
        <PrettySessionErrorMessage message={status.message} />
      </PopoverBody>
    </UncontrolledPopover>
  );

  if (status.state == "failed")
    return (
      <>
        <span
          ref={ref}
          className={cx("text-muted", "cursor-pointer")}
          tabIndex={0}
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          (More details.)
        </span>
        {popover}
      </>
    );
  return (
    <>
      <span ref={ref}>
        <FontAwesomeIcon icon={faInfoCircle} />
      </span>
      {popover}
    </>
  );
}

export function SessionStatusV2Title({
  session,
  launcher,
}: ActiveSessionTitleV2Props) {
  const { state } = session.status;
  const launcherCategory = sessionLauncherKindToCategory(session.session_type);
  const launcherDefinition = getLauncherCategoryDefinition(launcherCategory);
  const launcherText = launcherDefinition.text.inline;

  const text = getSessionStatusText(state, launcherText, !!launcher);

  return text ? <p className={cx("fst-italic", "mb-2")}>{text}</p> : null;
}

function getSessionStatusText(
  state:
    | "running"
    | "starting"
    | "stopping"
    | "failed"
    | "hibernated"
    | "succeeded",
  launcherText: string,
  hasLauncher: boolean,
): string | null {
  switch (state) {
    case "running":
      return hasLauncher
        ? `You are currently running a ${launcherText} from this launcher`
        : `You are currently running an orphan ${launcherText}`;
    case "starting":
      return `You are currently starting a ${launcherText} from this launcher`;
    case "stopping":
      return `You are currently deleting a ${launcherText} from this launcher`;
    case "hibernated":
      return `You have a paused ${launcherText} from this launcher`;
    case "failed":
      return `An error was encountered while attempting to launch this ${launcherText}.`;
    case "succeeded":
      return `You have a completed ${launcherText} from this launcher`;
    default:
      return null;
  }
}
interface SessionStatusV2TextProps {
  resourceClassId: number;
  startTimestamp: string;
  jobCompletedAt?: string | null;
  status: SessionStatus;
  launcherCategory: LauncherCategory;
  includeIcon?: boolean;
}

interface SessionStatusTextPartsProps {
  state: SessionStatusState;
  launcherCategory: LauncherCategory;
  launcherCategoryDefinition: LauncherCategoryDefinition;
  status: SessionStatus;
  startTimestamp: string;
  jobCompletedAt?: string | null;
  startTimeText: ReactNode;
  elapsedTimeText: ReactNode;
}

function GetSessionStatusTextIcon({
  state,
  status,
  startTimestamp,
  jobCompletedAt,
}: Pick<
  SessionStatusTextPartsProps,
  "state" | "status" | "startTimestamp" | "jobCompletedAt"
>) {
  switch (state) {
    case "running":
    case "starting":
    case "failed":
      return <Clock fontSize={16} className="flex-shrink-0" />;
    case "hibernated":
      return status.will_delete_at ? (
        <Hourglass fontSize={16} className="flex-shrink-0" />
      ) : (
        <Clock fontSize={16} className="flex-shrink-0" />
      );
    case "succeeded":
      return startTimestamp && jobCompletedAt ? (
        <Clock fontSize={16} className="flex-shrink-0" />
      ) : (
        <CheckCircleFill fontSize={16} className="flex-shrink-0" />
      );
    default:
      return null;
  }
}

function GetSessionStatusTextContent({
  state,
  launcherCategory,
  launcherCategoryDefinition,
  status,
  startTimeText,
  elapsedTimeText,
}: SessionStatusTextPartsProps) {
  const { text } = launcherCategoryDefinition;

  switch (state) {
    case "running":
      return launcherCategory === "session" ? (
        <>
          {text.state.running} {startTimeText}
        </>
      ) : (
        <>
          {text.state.running} for {elapsedTimeText}
        </>
      );
    case "starting":
      return (
        <>
          {text.state.starting} since {startTimeText}
        </>
      );
    case "hibernated": {
      const { will_delete_at, will_hibernate_at } = status;

      if (will_delete_at) {
        return (
          <>
            {text.state.hibernatedAndDelete}{" "}
            <TimeCaption
              datetime={will_delete_at}
              enableTooltip
              noCaption
              suffix=" "
              prefix=""
              removeRelativeSuffix="future"
            />
          </>
        );
      }

      const hibernationTimestamp = will_hibernate_at ?? "";
      if (hibernationTimestamp) {
        return (
          <>
            {text.state.hibernated}
            {" for "}
            <TimeCaption
              datetime={hibernationTimestamp}
              enableTooltip
              noCaption
            />
          </>
        );
      }

      return (
        <>
          {text.state.hibernated} <MissingHibernationInfo />
        </>
      );
    }
    case "failed":
      return launcherCategory === "session" ? (
        <>
          {text.state.failed} {"("}created {startTimeText}
          {")"}
        </>
      ) : (
        <>
          {text.state.failed} {startTimeText}
        </>
      );
    case "succeeded":
      return <>Completed {startTimeText}</>;
    default:
      return null;
  }
}

function SessionStatusV2Text({
  resourceClassId,
  startTimestamp,
  jobCompletedAt,
  status,
  launcherCategory,
  includeIcon = true,
}: SessionStatusV2TextProps) {
  const { state } = status;
  const launcherCategoryDefinition =
    getLauncherCategoryDefinition(launcherCategory);
  const startTimeText = (
    <TimeCaption datetime={startTimestamp} enableTooltip noCaption />
  );
  const elapsedTimeText = (
    <TimeCaption
      datetime={startTimestamp}
      enableTooltip
      noCaption
      removeRelativeSuffix="past"
    />
  );

  const textParts: SessionStatusTextPartsProps = {
    state,
    launcherCategory,
    launcherCategoryDefinition,
    status,
    startTimestamp,
    jobCompletedAt,
    startTimeText,
    elapsedTimeText,
  };

  const content = <GetSessionStatusTextContent {...textParts} />;
  if (!content) {
    return null;
  }

  const icon = <GetSessionStatusTextIcon {...textParts} />;
  const showQuotaInformation =
    state === "running" || state === "starting" || state === "hibernated";

  return (
    <div className={cx("d-flex", "flex-column", "gap-1")}>
      <div className={cx("d-flex", "align-items-center", "gap-2")}>
        {includeIcon && icon}
        <span>{content}</span>
      </div>
      {showQuotaInformation && (
        <div>
          <SessionStatusV2TextQuotaInformation
            resourceClassId={resourceClassId}
          />
        </div>
      )}
    </div>
  );
}

function SessionStatusV2TextQuotaInformation({
  resourceClassId,
}: {
  resourceClassId: SessionStatusV2TextProps["resourceClassId"];
}) {
  const pollingInterval = 60 * 1000; // 1 minute
  const { data: resourcePools } = useGetResourcePoolsQuery(
    {},
    { pollingInterval },
  );
  const resourceClass = resourcePools
    ?.flatMap((pool) => pool.classes)
    .find((cls) => cls.id === resourceClassId);
  if (!resourceClass || resourceClass.usage_hours_remaining == null)
    return null;

  return (
    <span className="text-muted">
      <UsageAvailable
        usageAvailableHours={resourceClass.usage_hours_remaining}
      />
    </span>
  );
}
